import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type {
	FormConsentPurpose,
	FormFieldIdentity,
	FormFieldType,
	FormFieldValidationRulesModel,
	FormSelectOptionModel,
} from '../../forms/form-types';
import { BaseEntity } from './base.entity';
import { FormEntity } from './form.entity';

/**
 * The live definition of one field on a form. The same shape as `registration_field`, so
 * the registration field builder carries over, plus what Forms adds (admin label,
 * identity, consent purpose). Every change also writes a new `form_version`, which is
 * what submissions point at.
 *
 * Nothing here is locked because of the kind of form it is (owner, 2026-09-27): what
 * limits a change is the minimum fields the form's active actions need. A field that has
 * ever held data is deactivated rather than deleted, and its key is never reused for a
 * different question.
 */
@Index('idx_form_field_form_active', ['fkFormId', 'isActive'], {})
@Entity('form_field')
export class FormFieldEntity extends BaseEntity {
	@Column('int', { name: 'fk_form__id', unsigned: true })
	public fkFormId!: number;

	// The key the answer is stored and submitted under.
	@Column('varchar', { name: 'fieldKey', length: 60 })
	public fieldKey!: string;

	// What the visitor reads. Long, because a consent label is a full sentence.
	@Column('varchar', { name: 'label', length: 1000 })
	public label!: string;

	// What staff read in the inbox, exports and mappings when the public label is long.
	@Column('varchar', { name: 'adminLabel', nullable: true, length: 120 })
	public adminLabel!: string | null;

	@Column('varchar', { name: 'fieldType', length: 50 })
	public fieldType!: FormFieldType;

	@Column('varchar', { name: 'placeholder', nullable: true, length: 255 })
	public placeholder!: string | null;

	@Column('varchar', { name: 'helpText', nullable: true, length: 500 })
	public helpText!: string | null;

	@Column({ type: 'boolean', name: 'isRequired', default: false })
	public isRequired!: boolean;

	@Column({ type: 'boolean', name: 'isActive', default: true })
	public isActive!: boolean;

	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	@Column('text', { name: 'defaultValue', nullable: true })
	public defaultValue!: string | null;

	@Column('json', { name: 'selectOptions', nullable: true })
	public selectOptions!: Array<FormSelectOptionModel> | null;

	// minLength / maxLength / pattern / min / max, enforced by the shared validator.
	@Column('json', { name: 'validationRules', nullable: true })
	public validationRules!: FormFieldValidationRulesModel | null;

	// What the field says about the submitter (email, phone, company, …).
	@Column('varchar', { name: 'identity', nullable: true, length: 20 })
	public identity!: FormFieldIdentity | null;

	// For consent fields: what the checkbox records agreement to.
	@Column('varchar', { name: 'consentPurpose', nullable: true, length: 20 })
	public consentPurpose!: FormConsentPurpose | null;

	// For a `terms` consent: the version (date the text last changed) of each policy.
	@Column('json', { name: 'policyVersions', nullable: true })
	public policyVersions!: Record<string, string> | null;

	@Column({
		type: 'boolean',
		name: 'includeInNotification',
		default: true,
	})
	public includeInNotification!: boolean;

	@ManyToOne(() => FormEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form__id', referencedColumnName: 'id' }])
	public form!: FormEntity;
}
